import type {
  CreateMeetingSharesPayload,
  CreateMeetingSharesResult,
  Meeting,
  MeetingAudioStatus,
  MeetingListQuery,
  MeetingListResponse,
  MeetingMinutesJobStatus,
  MeetingSharesResponse,
  MeetingUpdateResult,
  ShareableUsersResponse,
  UpdateMeetingPayload,
} from '@ai-meeting/shared'
import { API_BASE, apiClient, toApiError } from './api'
import { getServiceStrings } from './i18n'
import { normalizeSavedMeetingMinutes } from '@/utils/normalizeSavedMeetingMinutes'

export async function fetchMeetingList(query?: MeetingListQuery): Promise<MeetingListResponse> {
  try {
    const { data } = await apiClient.get<MeetingListResponse>('/api/meetings/meeting-list', {
      params: query,
    })
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetingList)
  }
}

export async function fetchDeletedMeetingList(
  query?: Omit<MeetingListQuery, 'status'>
): Promise<MeetingListResponse> {
  return fetchMeetingList({ ...query, status: 'deleted' })
}

export async function deleteMeeting(id: string): Promise<void> {
  try {
    await apiClient.delete(`/api/meetings/${id}`)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedDeleteMeetingService)
  }
}

export async function restoreMeeting(id: string): Promise<Meeting> {
  try {
    const { data } = await apiClient.post<Meeting>(`/api/meetings/${id}/restore`)
    return normalizeMeeting(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedRestoreMeetingService)
  }
}

export async function deleteMeetingPermanently(id: string): Promise<void> {
  try {
    await apiClient.delete(`/api/meetings/${id}/permanent`)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedPermanentDeleteMeetingService)
  }
}

export async function deleteMeetingsPermanently(ids: string[]): Promise<void> {
  try {
    await apiClient.delete('/api/meetings/deleted/permanent', { data: { ids } })
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedPermanentDeleteMeetingsService)
  }
}

export async function fetchMeetingById(id: string): Promise<Meeting> {
  try {
    const { data } = await apiClient.get<Meeting>(`/api/meetings/${id}`)
    return normalizeMeeting(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetingDetails)
  }
}

export async function fetchMeetingAudioStatus(
  id: string,
): Promise<MeetingAudioStatus> {
  try {
    const { data } = await apiClient.get<MeetingAudioStatus>(
      `/api/meetings/${id}/audio-status`,
    )
    return {
      ...data,
      audioUrl: data.audioUrl ? resolveAudioUrl(data.audioUrl) : null,
    }
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedLoadMeetings)
  }
}

/**
 * Create a new meeting row up-front, before the live recording UI starts.
 *
 * Server-side title uniqueness is enforced by the create endpoint itself;
 * a duplicate title surfaces as a 409 `ApiError` with `code: 'MEETING_TITLE_TAKEN'`.
 *
 * @param payload Title + language pair for the new meeting row.
 * @returns The persisted meeting (with backend-assigned UUID `id`).
 */
export async function createMeeting(payload: {
  title: string
  sourceLang: string
  targetLang: string
  sttProvider: Meeting['sttProvider']
  /** Backlog project key; omit for a meeting with no project. */
  backlogProjectKey?: string
  /** How the translation ran, for the per-function report (BRAI-677). */
  mode?: string
  /** Which kind of session the form picked (BRAI-677). */
  sessionKind?: string
  /** Room to declare to the AI proxy (BRAI-533); omit for an unshared meeting. */
  sharedRoomId?: string
}): Promise<Meeting> {
  try {
    const { data } = await apiClient.post<Meeting>('/api/meetings', payload)
    return normalizeMeeting(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveMeetingService)
  }
}

/**
 * Preflight check that a meeting title is available for the current user.
 * Resolves when free; throws a 409 `ApiError` with `code: 'MEETING_TITLE_TAKEN'`
 * when the title is already taken (including soft-deleted meetings).
 */
export async function checkMeetingTitle(title: string): Promise<void> {
  try {
    await apiClient.get('/api/meetings/title-availability', {
      params: { title },
    })
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedSaveMeetingService)
  }
}

/**
 * Takes the reader's meeting back out of the shared room it was merged into.
 *
 * Only the owner can; a meeting belonging to anyone else answers 404 rather
 * than 403, so the reply does not confirm the id exists. Leaving a meeting
 * that is in no room is not an error — the button can be pressed twice.
 *
 * Every real leave mints a fresh `soloRoomId` (handoff 84) so the socket can
 * reconnect without walking back into the room just left.
 *
 * @param id The reader's own meeting, never the room's id.
 * @returns The solo room code to declare on the next STT config frame.
 * @throws ApiError when the server refuses or cannot be reached.
 */
export async function leaveSharedRoom(id: string): Promise<{ soloRoomId: string | null }> {
  try {
    const response = await apiClient.delete<{ soloRoomId: string | null }>(
      `/api/meetings/${id}/shared-room`
    )
    if (response.status === 204) {
      return { soloRoomId: null }
    }
    return { soloRoomId: response.data?.soloRoomId ?? null }
  } catch (error) {
    throw toApiError(error, getServiceStrings().sharedRoomLeaveFailed)
  }
}

/**
 * The response omits `transcript`/`translationSegments` (see
 * {@link MeetingUpdateResult}) — merge it into an existing `Meeting` rather
 * than replacing it outright.
 */
export async function updateMeeting(
  id: string,
  payload: UpdateMeetingPayload
): Promise<MeetingUpdateResult> {
  try {
    const { data } = await apiClient.patch<MeetingUpdateResult>(`/api/meetings/${id}`, payload)
    return normalizeMeeting(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedUpdateMeeting)
  }
}

/** Share a meeting with other registered BrSE.ai users (owner or re-share). */
export async function createMeetingShares(
  meetingId: string,
  payload: CreateMeetingSharesPayload
): Promise<CreateMeetingSharesResult> {
  try {
    const { data } = await apiClient.post<CreateMeetingSharesResult>(
      `/api/meetings/${meetingId}/shares`,
      payload
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedShareMeetingService)
  }
}

/** Direct shares by the current user (Currently shared with + REMOVED). */
export async function fetchMeetingShares(
  meetingId: string
): Promise<MeetingSharesResponse> {
  try {
    const { data } = await apiClient.get<MeetingSharesResponse>(
      `/api/meetings/${meetingId}/shares`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedShareMeetingService)
  }
}

/** BrSE.ai users eligible to share with (exclude self + owner; flag alreadyShared). */
export async function fetchShareableUsers(
  meetingId: string,
  q?: string
): Promise<ShareableUsersResponse> {
  try {
    const { data } = await apiClient.get<ShareableUsersResponse>(
      `/api/meetings/${meetingId}/shareable-users`,
      { params: q?.trim() ? { q: q.trim() } : undefined }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedShareMeetingService)
  }
}

/** Revoke a direct share (+ cascade). */
export async function revokeMeetingShare(
  meetingId: string,
  userId: string
): Promise<void> {
  try {
    await apiClient.delete(`/api/meetings/${meetingId}/shares/${userId}`)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedRevokeShare)
  }
}

/** Hide a shared meeting from the caller's History (does not revoke). */
export async function hideMeetingFromHistory(meetingId: string): Promise<void> {
  try {
    await apiClient.post(`/api/meetings/${meetingId}/hide`)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedHideMeeting)
  }
}

export interface ShareMeetingMinutesByEmailPayload {
  locale: 'vi' | 'ja'
  emails: string[]
  attachment: Blob
  filename: string
}

export interface ShareMeetingMinutesByEmailResult {
  sent: string[]
}

/** Email meeting minutes DOCX (client-generated) to one or more recipients. */
export async function shareMeetingMinutesByEmail(
  meetingId: string,
  payload: ShareMeetingMinutesByEmailPayload
): Promise<ShareMeetingMinutesByEmailResult> {
  const formData = new FormData()
  formData.append('locale', payload.locale)
  formData.append('emails', JSON.stringify(payload.emails))
  formData.append('attachment', payload.attachment, payload.filename)

  try {
    const { data } = await apiClient.post<ShareMeetingMinutesByEmailResult>(
      `/api/meetings/${meetingId}/share-minutes-email`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
      }
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedShareMinutesEmail)
  }
}

/**
 * Ask the backend to generate this meeting's minutes and return as soon as the
 * job is accepted — the minutes themselves arrive later on the meeting row.
 *
 * Generation used to run in whichever tab ended the meeting, so closing it lost
 * the minutes for good; the server owns the work now and survives the tab
 * (BRAI-412). Safe to call more than once: a job already running is reported
 * back rather than started again.
 *
 * @param id Meeting to summarize.
 * @param options.regenerate Throw away minutes the meeting already has and
 *   write them again. The `READY` above is what a meeting that just changed
 *   project has to get past: its minutes were written under the old one.
 * @returns The job's state — `PENDING` while it runs, `READY` if the meeting
 *   already had minutes.
 */
export async function requestMeetingMinutes(
  id: string,
  options: { regenerate?: boolean } = {}
): Promise<MeetingMinutesJobStatus> {
  try {
    const { data } = await apiClient.post<MeetingMinutesJobStatus>(
      `/api/meetings/${id}/minutes${options.regenerate ? '?regenerate=true' : ''}`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedGenerateMeetingMinutes)
  }
}

/**
 * Queue minutes from the recording already uploaded for this meeting.
 *
 * Sends no file. It used to post the whole recording a second time, moments
 * after `/audio` had carried the same bytes, which doubled the upload and made
 * Retry download the recording only to send it straight back. The AI service
 * reads it from storage now, so there is nothing here but the id.
 *
 * The backend accepts an in-progress upload and waits for storage itself. A
 * 400 therefore means no upload was started, or that upload already failed.
 */
export async function requestMeetingMinutesFromAudio(
  id: string
): Promise<MeetingMinutesJobStatus> {
  try {
    const { data } = await apiClient.post<MeetingMinutesJobStatus>(
      `/api/meetings/${id}/minutes/audio`
    )
    return data
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedGenerateMeetingMinutes)
  }
}

/**
 * Ship one periodic slice of a meeting that is still being recorded, so a tab
 * that dies mid-meeting no longer takes the whole recording with it (BRAI-457).
 *
 * Best-effort by design: the caller ignores failures, because the finished
 * recording uploaded at End Meeting remains the primary copy and a part that
 * never arrives only shortens what could be recovered.
 *
 * @param id      Meeting being recorded.
 * @param index   Zero-based position of this part; the server stitches in this
 *   order, so callers must not reuse or skip one.
 * @param durationSeconds Meeting length when the part was cut — it becomes the
 *   recovered recording's playback length.
 * @param part    Audio recorded since the previous part.
 */
export async function uploadMeetingAudioPart(
  id: string,
  index: number,
  durationSeconds: number,
  part: Blob
): Promise<void> {
  const formData = new FormData()
  formData.append('index', String(index))
  formData.append('durationSeconds', String(durationSeconds))
  formData.append('part', part, `part-${index}.webm`)

  try {
    await apiClient.post(`/api/meetings/${id}/audio-parts`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedUploadMeetingAudio)
  }
}

export async function uploadMeetingAudio(id: string, audio: Blob): Promise<Meeting> {
  const formData = new FormData()
  formData.append('audio', audio, 'meeting-recording.webm')

  try {
    const { data } = await apiClient.post<Meeting>(`/api/meetings/${id}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return normalizeMeeting(data)
  } catch (error) {
    throw toApiError(error, getServiceStrings().failedUploadMeetingAudio)
  }
}

function normalizeMeeting<T extends Pick<Meeting, 'insights' | 'audioUrl'>>(meeting: T): T {
  return {
    ...meeting,
    insights: normalizeSavedMeetingMinutes(meeting.insights),
    audioUrl: meeting.audioUrl ? resolveAudioUrl(meeting.audioUrl) : null,
  }
}

function resolveAudioUrl(audioUrl: string): string {
  if (audioUrl.startsWith('http')) return audioUrl
  return `${API_BASE}${audioUrl}`
}
